import { ScanErrorKind, TokenKind } from '../../json/tokens';
import type { TextEdit } from '../../text/textEdit';
import { structuralTokens } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/** RFC 8259: everything below U+0020 must be escaped inside a string. */
const NAMED: Readonly<Record<number, string>> = {
  0x08: '\\b',
  0x09: '\\t',
  0x0a: '\\n',
  0x0c: '\\f',
  0x0d: '\\r',
};

function escapeOf(code: number): string {
  return NAMED[code] ?? `\\u${code.toString(16).padStart(4, '0')}`;
}

/**
 * Escape a raw control character inside a string literal: a literal tab pasted from a log
 * becomes `\t`, a `BEL` becomes `BEL`.
 *
 * Safe, and provably so: the value the document *means* is unchanged — `"x<TAB>y"` and
 * `"x\ty"` denote the same string — only its spelling becomes legal JSON. The rule reads
 * the characters of one `String` token whose boundaries the scanner drew, so nothing outside
 * a string literal is touched, and the escape sequence `\t` already present in a correct
 * document is two ordinary characters that no branch here matches.
 */
export const escapeControlCharacterRule: RepairRule = {
  id: 'escape-control-character',
  confidence: 'safe',
  priority: 33,
  resolves: ['string/control-character'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const proposals: RepairProposal[] = [];

    for (const token of structuralTokens(ctx.source, ctx.tokens)) {
      if (token.kind !== TokenKind.String) continue;
      if (token.scanError !== ScanErrorKind.InvalidCharacter) continue;

      const edits: TextEdit[] = [];
      for (let at = token.from; at < token.to; at += 1) {
        const code = ctx.source.charCodeAt(at);
        if (code < 0x20) edits.push({ from: at, to: at + 1, insert: escapeOf(code) });
      }
      if (edits.length === 0) continue;

      proposals.push({
        ruleId: 'escape-control-character',
        span: { from: token.from, to: token.to },
        edits,
        labelCode: 'fix.escape-control-character',
        labelParams: { count: edits.length },
        resolves: 'string/control-character',
      });
    }

    return proposals;
  },
};
